// Trip object schema — v2, European fixture-led (docs/planner/reference/ENGINE.md §4).
// Persisted as JSON in localStorage (tripStorage.js) and URL-encoded for
// share links (tripShareLink.js). Rendered as schema.org/TouristTrip on
// /planner/:tripId.
//
// v2 additions (2026-07-10): fixture-first match fields (competition_code,
// following_team, kickoff_local, fixture_status), feasibility travel inputs
// (origin.airports[], max_budget_pp, arrival_margin_pref, return_constraint),
// matchday legs[] (journey legs with trust cells), selected_option
// provenance, and a transfers budget category. All v1→v2 changes are
// additive; migrateTrip() upgrades stored trips and decoded share links.
//
// Architectural primitives kept (cheap to do now, painful to retrofit):
// - schema_version on every trip → enables one-shot migration
// - matchdays as an always-array (length-1 in v1/v2) → groundhop door open
// - anonymous owner UUID → migrating to authenticated/tenant is a value swap
// - source/refreshed/confidence on every pick + budget cell → api-trust rule

import { keyify } from './slugify';

// v4 additions (2026-07-23, Phase C "The Organiser"): people[] — the named
// party layer whose sheet the organiser forwards — and bookings[], the
// user-DECLARED reservations we render but never claim, price, or sum.
//
// NB: `people` is a NEW key. It is NOT the v3 `party` object, which stays
// exactly as it was (adults/children/has_tickets/end/driving_home) and remains
// the budget's count input. The Phase C draft proposed reusing `party` as an
// array; that would have destroyed those fields and broken the reducer,
// budgetBreakdown and the heuristics ctx. All v3→v4 changes are additive.

export const SCHEMA_VERSION = 4;

// Booking categories the organiser can log. 'other' is the escape hatch —
// there is always a booking type we didn't think of.
export const BOOKING_CATEGORIES = [
  'hotel',
  'flight',
  'rail',
  'transfer',
  'parking',
  'tour',
  'ticket',
  'other',
];

// Default IANA zone per booking category, used only to seed the field in the
// UI. It is ALWAYS stored explicitly on the booking: an .ics built from an
// implied zone ships wrong flight times, and a wrong time in a calendar is the
// same class of quiet lie as a stored last-train clock time.
//   'origin'      — the traveller's departure zone (outbound flight/rail)
//   'destination' — stadium-local (everything that happens once they arrive)
export const BOOKING_TZ_ANCHOR = {
  hotel: 'destination',
  flight: 'origin',
  rail: 'origin',
  transfer: 'destination',
  parking: 'origin',
  tour: 'destination',
  ticket: 'destination',
  other: 'destination',
};

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `trip_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const nowIso = () => new Date().toISOString();

/**
 * @typedef {Object} TripPick
 * @property {string} id - Composite stable id, e.g. "pub:editorial:the_sandon".
 * @property {string} [name]
 * @property {'live'|'editorial'|'estimate'|null} confidence
 * @property {string|null} source
 * @property {string|null} refreshed
 *
 * @typedef {Object} BudgetCell
 * @property {number} min
 * @property {number} max
 * @property {string|null} source
 * @property {string|null} refreshed
 * @property {'live'|'editorial'|'estimate'|null} confidence
 *
 * @typedef {Object} Trip
 * @property {string} id
 * @property {number} schema_version
 * @property {string} created_at
 * @property {string} updated_at
 * @property {{ id: string, kind: 'anonymous' }} owner
 * @property {Object} destination
 * @property {Array<Object>} matchdays
 * @property {Object} party - v3 counts (adults/children/…). Budget input.
 * @property {Array<Person>} people - v4 named party. Solo = exactly one.
 * @property {Array<Booking>} bookings - v4 user-declared reservations.
 * @property {Object} travel
 *
 * @typedef {Object} Person
 * @property {string} id
 * @property {string|null} name - First name / nickname is enough; never required.
 * @property {boolean} is_organiser - Exactly one person carries this.
 *
 * @typedef {Object} Booking
 * @property {string} id
 * @property {string} category - One of BOOKING_CATEGORIES.
 * @property {string|null} provider - Slug, e.g. 'booking.com'.
 * @property {string|null} provider_label - Display name.
 * @property {string|null} reference - User-supplied, free text.
 * @property {string|null} starts_local - 'YYYY-MM-DDTHH:MM' wall clock.
 * @property {string|null} ends_local
 * @property {string|null} tz - IANA zone the wall-clock times are in.
 * @property {string|null} detail - Flight no / hotel name / pickup point.
 * @property {Array<string>} for - Person ids; [] means everyone.
 * @property {number|null} cost - Declared, NEVER summed with our estimates.
 * @property {string|null} currency
 * @property {string} added_at
 * @property {Object} preferences
 * @property {Object} budget
 */

/**
 * Nights between an arrival and a departure date, as whole days. Pure string
 * date arithmetic (UTC midnights, no clock). Returns 0 for a same-day trip,
 * and null when either date is missing or invalid — callers treat null as
 * "no date range yet" (distinct from a real zero-night day trip).
 *
 * @param {string} arriveDate - 'YYYY-MM-DD'
 * @param {string} departDate - 'YYYY-MM-DD'
 * @returns {number|null}
 */
export const nightsFromDates = (arriveDate, departDate) => {
  if (!arriveDate || !departDate) return null;
  const a = Date.parse(`${arriveDate}T00:00:00Z`);
  const d = Date.parse(`${departDate}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(d)) return null;
  const nights = Math.round((d - a) / 86400000);
  return nights >= 0 ? nights : null;
};

/**
 * Build a fresh empty Trip seeded with an optional starting stadium.
 * @param {Object} [stadium] - From qualifying_stadiums.json: { name, city, country, ... }
 * @param {string} [stadium.name]
 * @param {string} [stadium.city]
 * @param {string} [stadium.country]
 * @param {number} [stadium.lat]
 * @param {number} [stadium.lng]
 * @param {string} [stadium.timezone]
 * @returns {Trip}
 */
export const createTrip = (stadium = null) => {
  const ts = nowIso();
  return {
    id: newId(),
    schema_version: SCHEMA_VERSION,
    created_at: ts,
    updated_at: ts,
    owner: { id: newId(), kind: 'anonymous' },

    destination: stadium
      ? {
          stadium_key: keyify(stadium.name),
          stadium_name: stadium.name,
          city: stadium.city || null,
          country: stadium.country || null,
          lat: stadium.lat ?? null,
          lng: stadium.lng ?? null,
          timezone: stadium.timezone || null,
        }
      : null,

    matchdays: [
      {
        match: {
          fixture_id: null,
          home_team: stadium?.home_teams?.[0] || null,
          away_team: null,
          following_team: null, // v2 — whose fan is travelling (away-end logic)
          competition: null,
          competition_code: null, // v2 — 'CL' | 'PL' | …
          matchday_date: null,
          kickoff_utc: null,
          kickoff_local: null, // v2 — display + rules run stadium-local
          venue_confirmed: false,
          fixture_status: null, // v2 — 'draw_only' | 'scheduled' | 'confirmed'
        },
        // v2 — journey legs (outbound/return flights, transfers…), each
        // carrying the standard trust cell. Populated by E2 adapters +
        // E3 option selection; empty array until then.
        legs: [],
        picks: {
          hotel: null,
          pre_match_pub: null,
          post_match_venue: null,
          parking: null,
          transit: null,
        },
      },
    ],

    // v3 — counts. The budget's input (buildBreakdown ctx, perPersonTotal
    // share). Untouched by v4; `people` below is a separate, named layer.
    party: {
      adults: 2,
      children: 0,
      has_tickets: false,
      end: 'neutral',
      driving_home: false,
    },

    // v4 — the named party. Always at least the organiser, so "solo" is a
    // party of one rather than a special case. The UI stays hidden until a
    // second person is added (C0.6 progressive disclosure).
    people: [createPerson({ is_organiser: true })],

    // v4 — user-DECLARED reservations. We render them; we never claim,
    // price, verify or sum them. Empty is the honest default: nothing here
    // is ever inferred from a click.
    bookings: [],

    travel: {
      origin: { city: null, country: null, airport_code: null, airports: [] }, // airports[] v2 — home/departure IATA list (parking)
      arrival_airport: null, // v3 — the inbound UK arrival airport (IATA); joins to a ground block's airport_links
      arrival_mode: 'train',
      nights: 1,
      arrive_date: null,
      depart_date: null,
      max_budget_pp: null, // v2 — budget as an INPUT (candidate filter), nullable
      arrival_margin_pref: 'standard', // v2 — 'safe' | 'standard' | 'tight' (R1 thresholds)
      return_constraint: 'flexible', // v2 — 'same_night' | 'next_morning' | 'flexible' (R2)
    },

    preferences: {
      food_type: 'pub',
      drinks: 'before',
    },

    // v2 — provenance for "why this trip looks like this": which of the
    // three ranked options the user selected (E3). Null until an option
    // is chosen. Candidate options themselves are ephemeral (never stored).
    selected_option: null,

    budget: {
      currency: null,
      per_person: { min: 0, max: 0 },
      breakdown: {
        tickets: emptyBudgetCell(),
        hotel: emptyBudgetCell(),
        flight: emptyBudgetCell(),
        transfers: emptyBudgetCell(), // v2 — airport/ground transfers
        transit: emptyBudgetCell(),
        food: emptyBudgetCell(),
        drinks: emptyBudgetCell(),
        parking: emptyBudgetCell(),
      },
      last_calculated: null,
    },
  };
};

const emptyBudgetCell = () => ({
  min: 0,
  max: 0,
  source: null,
  refreshed: null,
  confidence: null,
});

/**
 * A member of the travelling party. Name is optional by design — the organiser
 * is typing OTHER PEOPLE'S names, so a first name or nickname is enough and a
 * full name is never demanded (C7 third-party PII).
 * @param {Object} [init]
 * @param {string} [init.name]
 * @param {boolean} [init.is_organiser]
 * @returns {Person}
 */
export const createPerson = ({ name = null, is_organiser = false } = {}) => ({
  id: newId(),
  name: name || null,
  is_organiser: !!is_organiser,
});

/**
 * A user-declared booking. Every field except `category` is optional: a
 * booking that says only "Booking.com, ref ABC123" is valid and useful, and
 * demanding more is how capture dies.
 *
 * `cost` is DECLARED money — the user's own figure for something they bought
 * elsewhere. It is displayed beside our estimates and never added to them
 * (see budgetBreakdown.perPersonTotal, which excludes the 'declared' class).
 *
 * @param {Object} [init]
 * @returns {Booking}
 */
export const createBooking = (init = {}) => {
  const category = BOOKING_CATEGORIES.includes(init.category) ? init.category : 'other';
  return {
    id: newId(),
    category,
    provider: init.provider || null,
    provider_label: init.provider_label || null,
    reference: init.reference || null,
    starts_local: init.starts_local || null,
    ends_local: init.ends_local || null,
    // Explicit zone or nothing. We never guess one — an unset tz renders as
    // a plain wall-clock time and is skipped by the .ics export rather than
    // exported at a zone we invented.
    tz: init.tz || null,
    detail: init.detail || null,
    for: Array.isArray(init.for) ? init.for.filter(Boolean) : [],
    cost: typeof init.cost === 'number' && Number.isFinite(init.cost) ? init.cost : null,
    currency: init.currency || null,
    added_at: nowIso(),
  };
};

/**
 * Migrate a trip from a prior schema_version. Mechanical, additive-only —
 * v1 trips (localStorage AND decoded share links) gain v2 fields with
 * defaults; nothing is dropped. Idempotent: v2 in → v2 out unchanged.
 * @param {Object} raw
 * @returns {Trip}
 */
/**
 * Is a trip missing the v4 arrays? A trip can arrive at the CURRENT version
 * without them — the share-link encoder strips `people`/`bookings` so personal
 * data never travels in a URL, and the decoded payload still says v4. Without
 * this check migrateTrip's early return hands that trip straight back and the
 * arrays stay undefined forever, because the version can never re-trigger the
 * v3→v4 step. The recipient then edits a trip with no organiser.
 */
const needsV4Fill = (t) =>
  !Array.isArray(t?.people) || t.people.length === 0 || !Array.isArray(t?.bookings);

// Seed the v4 arrays in place. Idempotent, and never touches an existing
// organiser.
const fillV4 = (t) => {
  if (!Array.isArray(t.people) || t.people.length === 0) {
    t.people = [createPerson({ is_organiser: true })];
  }
  if (!Array.isArray(t.bookings)) t.bookings = [];
};

export const migrateTrip = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  // Pass a complete current-version trip straight back by reference. A
  // current-version trip MISSING the v4 arrays falls through to be normalised.
  if (raw.schema_version === SCHEMA_VERSION && !needsV4Fill(raw)) return raw;

  // Deep-copy so migration never mutates the caller's stored object. Each step
  // upgrades exactly one schema version and falls through to the next, so ANY
  // older trip is chained forward to current (v1 → v2 → v3).
  const t = JSON.parse(JSON.stringify(raw));

  if (t.schema_version === 1) {
    t.schema_version = 2;

    // Match: fixture-first fields (docs/planner/reference/ENGINE.md §4.1). Any v1
    // trip with a matchday date was a manually-entered fixture → 'scheduled'.
    t.matchdays = (t.matchdays || []).map((md) => ({
      ...md,
      match: {
        following_team: null,
        competition_code: null,
        kickoff_local: null,
        fixture_status: md?.match?.matchday_date ? 'scheduled' : null,
        ...md.match,
      },
      legs: Array.isArray(md.legs) ? md.legs : [],
    }));

    // Travel: feasibility inputs (§4.2). Seed airports[] from the single
    // v1 airport_code when present.
    const origin = t.travel?.origin || {};
    t.travel = {
      max_budget_pp: null,
      arrival_margin_pref: 'standard',
      return_constraint: 'flexible',
      ...t.travel,
      origin: {
        ...origin,
        airports: Array.isArray(origin.airports)
          ? origin.airports
          : origin.airport_code
            ? [origin.airport_code]
            : [],
      },
    };

    // Option provenance (§4.4) + transfers budget category.
    if (!('selected_option' in t)) t.selected_option = null;
    if (t.budget?.breakdown && !t.budget.breakdown.transfers) {
      t.budget.breakdown.transfers = emptyBudgetCell();
    }
  }

  if (t.schema_version === 2) {
    t.schema_version = 3;
    // v3 (A3): the inbound UK arrival airport — additive, null default.
    t.travel = { ...t.travel, arrival_airport: t.travel?.arrival_airport ?? null };
  }

  if (t.schema_version === 3) {
    t.schema_version = 4;
    // v4 (Phase C): the named party + declared bookings. STRICTLY ADDITIVE —
    // `t.party` (the v3 counts object) is deliberately not read or written
    // here. A v3 trip gains a party of one and no bookings, which is exactly
    // what it already was: one anonymous planner who has told us nothing
    // about what they've booked. Nothing is dropped, nothing is invented,
    // and no existing surface renders differently.
    fillV4(t);
  }

  // Normalise any trip that is AT the current version but arrived without the
  // v4 arrays — the stripped-share-link case above. Deliberately scoped to the
  // current version: an unknown/future trip is returned untouched below rather
  // than being "helpfully" reshaped.
  if (t.schema_version === SCHEMA_VERSION) fillV4(t);

  // Landed on current, or an unknown/future version we won't corrupt.
  return t.schema_version === SCHEMA_VERSION ? t : raw;
};
